import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://bikerentflorence.it/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// Aggiunge automaticamente il token JWT se presente
api.interceptors.request.use(config => {
  const token = localStorage.getItem('brf_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Gestione errori globale
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('brf_token');
      // Solo redirect se siamo in una pagina protetta
      const path = window.location.pathname;
      if (path.startsWith('/dashboard') || path.startsWith('/admin')) {
        window.location.href = '/';
      }
    }
    return Promise.reject(err);
  }
);

// ── PUBLIC API ──
export const publicApi = {
  getSettings:     ()         => api.get('/settings/public'),
  getAvailability: (date, dur)=> api.get(`/bikes/available?date=${date}&duration=${dur}`),
  getOptionals:    ()         => api.get('/admin/optionals'),
  getRates:        ()         => api.get('/admin/rates'),
  getItineraries:  (type)     => api.get(`/client/itineraries${type ? `?bike_type=${type}` : ''}`),
  getInfo:         ()         => api.get('/client/info'),
};

// ── BOOKING API ──
export const bookingApi = {
  create: (formData) => api.post('/bookings', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  get:    (ref)      => api.get(`/bookings/${ref}`),
};

// ── PAYMENT API ──
export const paymentApi = {
  createStripeIntent: (bookingRef) => api.post('/payments/stripe/create-intent', { booking_ref: bookingRef }),
  confirmStripe:      (data)       => api.post('/payments/stripe/confirm', data),
  createPaypalOrder:  (bookingRef) => api.post('/payments/paypal/create-order', { booking_ref: bookingRef }),
  capturePaypal:      (data)       => api.post('/payments/paypal/capture-order', data),
};

// ── AUTH API ──
export const authApi = {
  login:    (email, password) => api.post('/auth/login', { email, password }),
  me:       ()                => api.get('/auth/me'),
  register: (data)            => api.post('/auth/client-register', data),
};

// ── CLIENT API ──
export const clientApi = {
  dashboard:   ()    => api.get('/client/dashboard'),
  bookings:    ()    => api.get('/client/bookings'),
  booking:     (ref) => api.get(`/client/bookings/${ref}`),
  contract:    (ref) => api.get(`/client/bookings/${ref}/contract`, { responseType: 'blob' }),
  profile:     ()    => api.get('/client/profile'),
  updateProfile: (d) => api.put('/client/profile', d),
};

// ── ADMIN API ──
export const adminApi = {
  dashboard:   ()           => api.get('/admin/dashboard'),
  reports:     (period)     => api.get(`/admin/reports/revenue?period=${period}`),
  bookings:    (params)     => api.get('/bookings', { params }),
  updateStatus:(ref, status)=> api.patch(`/bookings/${ref}/status`, { status }),

  // Tariffe
  getRates:    ()           => api.get('/admin/rates'),
  updateRates: (rates)      => api.put('/admin/rates', { rates }),
  addRate:     (data)       => api.post('/admin/rates/special', data),
  deleteRate:  (id)         => api.delete(`/admin/rates/${id}`),

  // Optional
  getOptionals:    ()       => api.get('/admin/optionals'),
  addOptional:     (data)   => api.post('/admin/optionals', data),
  updateOptional:  (id, d)  => api.put(`/admin/optionals/${id}`, d),
  deleteOptional:  (id)     => api.delete(`/admin/optionals/${id}`),

  // Bici
  getBikes:        ()       => api.get('/bikes'),
  addBike:         (data)   => api.post('/bikes', data),
  updateBike:      (id, d)  => api.put(`/bikes/${id}`, d),
  updateBikeStatus:(id, s)  => api.patch(`/bikes/${id}/status`, { status: s }),
  deleteBike:      (id)     => api.delete(`/bikes/${id}`),
  checkin:         (ref)    => api.post('/bikes/checkin', { booking_ref: ref }),
  checkout:        (data)   => api.post('/bikes/checkout', data),
  activeRentals:   ()       => api.get('/bikes/operator/active'),

  // Itinerari
  getItineraries:  ()       => api.get('/admin/itineraries'),
  addItinerary:    (data)   => api.post('/admin/itineraries', data),
  updateItinerary: (id, d)  => api.put(`/admin/itineraries/${id}`, d),
  deleteItinerary: (id)     => api.delete(`/admin/itineraries/${id}`),

  // Impostazioni
  getSettings:     ()       => api.get('/settings'),
  updateSettings:  (data)   => api.put('/settings', data),
  saveStripe:      (data)   => api.put('/settings/stripe', data),
  testStripe:      ()       => api.post('/settings/stripe/test'),
  savePaypal:      (data)   => api.put('/settings/paypal', data),
  testPaypal:      ()       => api.post('/settings/paypal/test'),

  // Utenti
  getUsers:    ()           => api.get('/admin/users'),
  addUser:     (data)       => api.post('/admin/users', data),
  updateUser:  (id, d)      => api.put(`/admin/users/${id}`, d),
  deleteUser:  (id)         => api.delete(`/admin/users/${id}`),

  // Cauzione
  releaseCaution: (ref)     => api.post('/payments/stripe/release-caution', { booking_ref: ref }),
  chargeCaution:  (data)    => api.post('/payments/stripe/charge-caution', data),
};

export default api;
